import type { InteractionType, InterestLevel, PipelineStage } from '@performance-crm/shared';
import {
  whatsappLink,
  CURRENCY_SYMBOLS,
  dateKey,
  DEFAULT_TIMEZONE,
  FINANCING_LABELS,
  formatMoney,
  fromWallInput,
  isDayKey,
  PIPELINE_STAGE_LABELS,
  PROJECT_PROGRAM_LABELS,
  toWallInput,
  type ProjectCommercial,
  APPOINTMENT_PLATFORM_LABELS,
  type AppointmentInfo,
} from '@performance-crm/shared';

const TZ = DEFAULT_TIMEZONE;
const dateTime = new Intl.DateTimeFormat('es-PE', {
  timeZone: TZ,
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});
const dateOnly = new Intl.DateTimeFormat('es-PE', {
  timeZone: TZ,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});
// Fechas puras ('1990-05-14', sin hora): se pintan tal cual, sin pasar por ninguna zona (ux-transversal-11).
const pureDate = new Intl.DateTimeFormat('es-PE', {
  timeZone: 'UTC',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});
const timeOnly = new Intl.DateTimeFormat('es-PE', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
});
const dateTimeFull = new Intl.DateTimeFormat('es-PE', {
  timeZone: TZ,
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** Símbolos de moneda de cara al usuario (el código ISO sigue siendo el valor que viaja a la API). */
export const CURRENCY_SYMBOL: Record<string, string> = CURRENCY_SYMBOLS;

export const fmt = {
  dateTime: (iso: string | null | undefined) => (iso ? dateTime.format(new Date(iso)) : '—'),
  dateTimeFull: (iso: string | null | undefined) =>
    iso ? dateTimeFull.format(new Date(iso)) : '—',
  /** Instante ISO → fecha en Lima; una fecha pura AAAA-MM-DD (nacimiento, entrega) se muestra sin cambio de zona. */
  date: (iso: string | null | undefined) =>
    iso
      ? isDayKey(iso)
        ? pureDate.format(new Date(`${iso}T00:00:00Z`))
        : dateOnly.format(new Date(iso))
      : '—',
  time: (iso: string | null | undefined) => (iso ? timeOnly.format(new Date(iso)) : '—'),
  /** "hace 12 min", "hace 3 h", "hace 2 d" */
  ago: (iso: string | null | undefined, now = Date.now()) => {
    if (!iso) return '—';
    const min = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
    if (min < 1) return 'ahora';
    if (min < 60) return `hace ${min} min`;
    const h = Math.round(min / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.round(h / 24);
    return `hace ${d} d`;
  },
  minutes: (m: number) =>
    m < 60 ? `${m} min` : m < 1440 ? `${Math.round(m / 60)} h` : `${Math.round(m / 1440)} d`,
  number: (n: number) => new Intl.NumberFormat('es-PE').format(n),
  /**
   * "US$ 152,000" / "S/ 350,000.50" (como se escriben en Perú) a partir de un decimal serializado o número: los
   * centavos se muestran cuando los hay (ux-comercial-25); `{ decimals: 0 }` para resúmenes en entero.
   */
  money: (
    v: string | number | null | undefined,
    currency = 'USD',
    opts?: { decimals?: 'auto' | 0 },
  ) => formatMoney(v, currency, opts),
  pct: (v: number | null | undefined) =>
    v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`,
};

/** Valor para <input type="date"> (YYYY-MM-DD) desde una fecha pura o un ISO con hora en la zona dada. */
export function toDateInput(iso: string | null | undefined, tz: string = TZ): string {
  if (!iso) return '';
  return isDayKey(iso) ? iso : dateKey(new Date(iso), tz);
}

/**
 * «Hoy» (AAAA-MM-DD) en la zona de la empresa, no en la del navegador ni en UTC (ux-transversal-21): pasadas las
 * 7 p. m. de Lima, UTC ya va en «mañana».
 */
export function todayInTz(tz: string = TZ, now: Date = new Date()): string {
  return dateKey(now, tz);
}

/**
 * Valor para <input type="datetime-local"> con la hora de pared de la zona de la empresa. Lo que la persona ve y
 * escribe en el campo es hora de Lima (o de la zona de su empresa), sin importar dónde esté su laptop.
 */
export function toLocalInput(date: Date, tz: string = TZ): string {
  return toWallInput(date, tz);
}
/** Instante ISO de un valor de datetime-local leído como hora de la zona de la empresa. */
export function fromLocalInput(value: string, tz: string = TZ): string {
  return fromWallInput(value, tz).toISOString();
}
/** Mañana a la misma hora (en punto), entre 09:00 y 18:00 de la zona de la empresa. */
export function defaultNextTask(tz: string = TZ): string {
  const wall = toWallInput(new Date(Date.now() + 24 * 3_600_000), tz);
  const hour = Math.min(18, Math.max(9, Number(wall.slice(11, 13))));
  return `${wall.slice(0, 10)}T${String(hour).padStart(2, '0')}:00`;
}

export const STAGE_LABEL: Record<PipelineStage, string> = PIPELINE_STAGE_LABELS;
export const STAGE_TONE: Record<
  PipelineStage,
  'muted' | 'primary' | 'warning' | 'success' | 'danger' | 'default'
> = {
  NUEVO: 'muted',
  CONTACTADO: 'primary',
  CITA: 'primary',
  VISITA: 'warning',
  PROFORMA: 'warning',
  SEPARACION: 'success',
  VENTA: 'success',
  PERDIDO: 'danger',
};

export const FOLLOWUP_LABEL: Record<string, string> = {
  NO_GESTIONADO: 'Sin gestionar',
  AL_DIA: 'Al día',
  VENCIDO: 'Vencido',
  INACTIVO: 'Cerrado',
};
export const BAND_LABEL: Record<string, string> = {
  HOY: 'hoy',
  AYER: 'ayer',
  DIAS_2_7: '2–7 días',
  MAS_7: '+7 días',
};

export function followupTone(
  state: string,
  band: string | null,
): 'success' | 'warning' | 'danger' | 'muted' {
  if (state === 'AL_DIA') return 'success';
  if (state === 'NO_GESTIONADO') return 'warning';
  if (state === 'VENCIDO') return band === 'HOY' || band === 'AYER' ? 'warning' : 'danger';
  return 'muted';
}
export function followupText(state: string, band: string | null, days: number) {
  if (state === 'VENCIDO')
    return `Vencido ${band === 'HOY' ? 'hoy' : band === 'AYER' ? 'ayer' : `hace ${days} d`}`;
  return FOLLOWUP_LABEL[state] ?? state;
}

export const INTEREST_LABEL: Record<InterestLevel, string> = {
  ALTO: 'Alto',
  MEDIO: 'Medio',
  BAJO: 'Bajo',
  SIN_DEFINIR: 'Sin definir',
};
export const TYPE_LABEL: Record<InteractionType, string> = {
  LLAMADA: 'Llamada',
  WHATSAPP: 'WhatsApp',
  MAIL: 'Correo',
  SMS: 'SMS',
  REUNION: 'Reunión',
  VISITA: 'Visita',
  SE_CITO: 'Cita',
  PROFORMA: 'Proforma',
  NOTA: 'Nota',
};

export function waLink(phoneNormalized: string | null, name?: string, project?: string) {
  if (!phoneNormalized) return null;
  const text = name
    ? `Hola ${name.split(' ')[0]}, te escribo de ${project ?? 'Performance'} para continuar con tu consulta.`
    : undefined;
  return whatsappLink(phoneNormalized, text);
}
export function telLink(phoneNormalized: string | null) {
  return phoneNormalized ? `tel:+51${phoneNormalized}` : null;
}

/** Resumen de la ficha comercial de un proyecto: "Separación US$ 1,000 · inicial 10 % en 3 cuotas · Hipotecario · BCP". */
export function describeProjectCommercial(c: ProjectCommercial | null | undefined): string {
  if (!c) return '';
  const parts: string[] = [];
  if (c.reservationAmount)
    parts.push(`Separación ${fmt.money(c.reservationAmount, c.reservationCurrency)}`);
  if (c.initialFeePercent)
    parts.push(
      `inicial ${c.initialFeePercent}%${c.initialInstallments && c.initialInstallments > 1 ? ` en ${c.initialInstallments} cuotas` : ''}`,
    );
  if (c.financingTypes.length)
    parts.push(c.financingTypes.map((t) => FINANCING_LABELS[t] ?? t).join(', '));
  if (c.banks.length) parts.push(c.banks.join(', '));
  if (c.programs.length) parts.push(c.programs.map((p) => PROJECT_PROGRAM_LABELS[p]).join(', '));
  return parts.join(' · ');
}

/** "Presencial · Sala de ventas Pardo" o "Virtual · Google Meet". */
export function describeAppointment(a: AppointmentInfo | null | undefined): string {
  if (!a) return '';
  if (a.mode === 'VIRTUAL')
    return `Virtual${a.platform ? ` · ${APPOINTMENT_PLATFORM_LABELS[a.platform]}` : ''}`;
  return `Presencial${a.location ? ` · ${a.location}` : ''}`;
}
